import { useMemo } from "react";
import { Paperclip, Pencil, RefreshCw, Trash2, Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/common";
import { formatEntryDate, formatMetricValue } from "../../domains/dataEntry";
import { getFieldList, FIELD_LABELS } from "../../domains/dataEntry.validation";
import { useAuthStore } from "@/features/auth";
import { cn } from "@/lib/utils";
import { Can } from "@/hooks/useCan/Can";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const getMonthFromReportingDate = (reportingDate) => {
  if (!reportingDate) return "—";
  const date = new Date(reportingDate);
  if (Number.isNaN(date.getTime())) return "—";
  return MONTH_NAMES[date.getMonth()] || "—";
};

const renderCell = (entry, field) => {
  if (field === "label") return entry.label || "—";
  if (field === "reportingMonth") return formatEntryDate(entry.reportingDate);
  if (field === "entryDate") return formatEntryDate(entry.entryDate);
  if (field === "value") {
    return entry.value == null || entry.value === ""
      ? "—"
      : formatMetricValue(entry.value);
  }
  if (field === "unit") return entry.unit || "—";
  return entry[field] || "—";
};

const cellClassName = (field) => {
  if (field === "value") return "font-semibold";
  if (field === "label") return "";
  return "text-muted-foreground";
};

const EntryTable = ({
  entries,
  inputType = "value",
  editable = true,
  onEdit,
  onDelete,
  onProof,
  onSync,
  permissions,
  sort,
  onSort,
}) => {
  const fields = getFieldList(inputType);

  const columns = useMemo(() => {
    const fieldColumns = fields.map((field) => {
      const label = FIELD_LABELS[field] || field;

      return {
        key: field,
        header: label,
        render: (entry) => renderCell(entry, field),
        cellClassName: cellClassName(field),
        sortable: true,
        showSortIndicators: true,
      };
    });

    const hasCrudActions =
      permissions.includes("esg_data_entry:update") ||
      permissions.includes("esg_data_entry:delete");
    const hasSourceActions = entries.some(
      (entry) => entry?.nxmapImported || entry?.internalId,
    );
    const hasActions = hasCrudActions || hasSourceActions;

    const actionsColumn = hasActions
      ? {
          key: "actions",
          header: "Actions",
          render: (entry) => {
            const entryEditable =
              editable && !entry.nxmapImported && !entry.internalId;
            return (
              <div className="flex items-center gap-2">
                {entry.nxmapImported && (
                  <>
                    <Badge
                      variant="secondary"
                      className="text-[10px] gap-0.5 px-1.5 py-0"
                    >
                      <Zap size={10} />
                      NXMap
                    </Badge>
                    <Can
                      resource="esg_data_entry"
                      action="create"
                      userPermissions={permissions}
                    >
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                        onClick={() => onSync?.(entry._id)}
                      >
                        <RefreshCw size={12} />
                        Sync
                      </button>
                    </Can>
                  </>
                )}
                {!!entry.internalId && (
                  <>
                    <Badge
                      variant="dataEntry"
                      className="text-[10px] gap-0.5 px-1.5 py-0"
                    >
                      GTool
                    </Badge>
                  </>
                )}
                <Can
                  resource="esg_data_entry"
                  action="update"
                  userPermissions={permissions}
                >
                  <button
                    type="button"
                    className="relative text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => onProof?.(entry)}
                  >
                    <Paperclip size={16} />
                    {entry.proofs?.length > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground">
                        {entry.proofs.length}
                      </span>
                    )}
                  </button>
                </Can>
                {entryEditable && (
                  <>
                    <Can
                      resource="esg_data_entry"
                      action="update"
                      userPermissions={permissions}
                    >
                      <button
                        type="button"
                        className="text-amber-500 hover:text-amber-600"
                        onClick={() => onEdit?.(entry)}
                      >
                        <Pencil size={16} />
                      </button>
                    </Can>
                    <Can
                      resource="esg_data_entry"
                      action="delete"
                      userPermissions={permissions}
                    >
                      <button
                        type="button"
                        className="text-destructive hover:text-destructive/80"
                        onClick={() => onDelete?.(entry._id)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </Can>
                  </>
                )}
              </div>
            );
          },
        }
      : null;

    return actionsColumn ? [...fieldColumns, actionsColumn] : fieldColumns;
  }, [
    entries,
    fields,
    editable,
    onEdit,
    onDelete,
    onProof,
    onSync,
    permissions,
  ]);

  return (
    <div className="overflow-x-auto">
      <DataTable
        columns={columns}
        data={entries}
        sort={sort}
        onSort={onSort}
        filtersEnabled={false}
        getRowKey={(row) => row._id}
        emptyMessage="No entries found."
      />
    </div>
  );
};

export default EntryTable;
